import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { hasBackendMessage } from '@app/core/utils/backend-error';
import {
  CreateSubcategoryRequest,
  CreateSubcategoriesBulkRequest,
  SubcategoryModel,
  SubcategorySearchRequest,
  SubcategorySearchResponse,
} from '@app/core/models/product-parameters/subcategory.model';

const EMPTY_SUBCATEGORIES_PAGE: SubcategorySearchResponse = {
  currentPageNumber: 0,
  pageSize: 10,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class SubcategoryService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/subcategories`;
  }

  search(
    page = 0,
    size = 10,
    body: SubcategorySearchRequest = {}
  ): Observable<SubcategorySearchResponse> {
    this.isLoading.set(true);

    const params = new HttpParams().set('page', page.toString()).set('size', size.toString());

    return this.http
      .post<SubcategorySearchResponse>(`${this.baseUrl}/search`, body, { params })
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении подкатегорий:', error);
          this.translate.get('Ошибка при получении подкатегорий').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of({ ...EMPTY_SUBCATEGORIES_PAGE, pageSize: size });
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getById(id: string): Observable<SubcategoryModel | null> {
    if (!id) return of(null);
    this.isLoading.set(true);
    return this.http.get<SubcategoryModel>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении подкатегории:', error);
        this.translate.get('Ошибка при получении подкатегории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  create(body: CreateSubcategoryRequest): Observable<SubcategoryModel> {
    this.isLoading.set(true);
    return this.http.post<SubcategoryModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании подкатегории:', error);
        // Текст бэкенда («…already exists…») показывает диалог — без второго общего тоста
        if (!hasBackendMessage(error)) {
          this.translate.get('Ошибка при создании подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  createBulk(body: CreateSubcategoriesBulkRequest): Observable<SubcategoryModel[]> {
    this.isLoading.set(true);
    return this.http.post<SubcategoryModel[]>(`${this.baseUrl}/bulk`, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании подкатегорий:', error);
        // Текст бэкенда («…already exists…») показывает диалог — без второго общего тоста
        if (!hasBackendMessage(error)) {
          this.translate.get('Ошибка при создании подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(id: string, body: CreateSubcategoryRequest): Observable<SubcategoryModel> {
    this.isLoading.set(true);
    return this.http.put<SubcategoryModel>(`${this.baseUrl}/${id}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении подкатегории:', error);
        // Текст бэкенда («…already exists…») показывает диалог — без второго общего тоста
        if (!hasBackendMessage(error)) {
          this.translate.get('Ошибка при обновлении подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении подкатегории:', error);
        this.translate.get('Ошибка при удалении подкатегории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activate(id: string): Observable<SubcategoryModel> {
    this.isLoading.set(true);
    return this.http.patch<SubcategoryModel>(`${this.baseUrl}/${id}/activate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при активации подкатегории:', error);
        this.translate.get('Ошибка при активации подкатегории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivate(id: string): Observable<SubcategoryModel> {
    this.isLoading.set(true);
    return this.http.patch<SubcategoryModel>(`${this.baseUrl}/${id}/deactivate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при деактивации подкатегории:', error);
        this.translate.get('Ошибка при деактивации подкатегории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }
}
